import * as R from 'remeda';
import {
  type AnnotationFieldChange,
  type AuditLogEntry,
  isAnnotationStatusChange,
} from '@rossum/api-client/auditLogs';
import {
  type ActivityRowContext,
  isManualSource,
  toActivitySource,
} from '../../core';

const REVIEWING = 'reviewing';
const TO_REVIEW = 'to_review';

const statusChanges = (entry: AuditLogEntry) =>
  (entry.content.changes.annotation?.data ?? []).filter(
    isAnnotationStatusChange
  );

const entersReviewing = (entry: AuditLogEntry) =>
  statusChanges(entry).some(change => change.value === REVIEWING);

const leavesReviewing = (entry: AuditLogEntry) =>
  statusChanges(entry).some(change => change.previousValue === REVIEWING);

const isUserOpen = (entry: AuditLogEntry) =>
  isManualSource(toActivitySource(entry)) && entersReviewing(entry);

// Duplicate opens with no exit between them describe one session — only the
// oldest of them is its start.
export const isSessionStart = (
  entry: AuditLogEntry,
  { entries, index }: ActivityRowContext
) =>
  isUserOpen(entry) &&
  !R.pipe(
    entries,
    R.drop(index + 1),
    R.takeWhile(older => !leavesReviewing(older))
  ).some(isUserOpen);

// Entries are newest-first: the session's end is the nearest lower index that
// left `reviewing`, its start the nearest higher index that entered it.
export const findClosedAt = ({
  entries,
  index,
}: ActivityRowContext): string | null =>
  R.pipe(entries, R.take(index), R.findLast(leavesReviewing))?.timestamp ??
  null;

const closesUserSession = ({ entries, index }: ActivityRowContext) => {
  const nextOpening = R.pipe(
    entries,
    R.drop(index + 1),
    R.find(entersReviewing)
  );
  return nextOpening ? isUserOpen(nextOpening) : false;
};

// Either half of an open/close pair — rendered by the opened adapter, not as a
// status change. Exits that carry a decision (confirmed, postponed, …) are
// not part of the pair.
export const isOpenSessionTransition = (
  change: Extract<AnnotationFieldChange, { field: 'status' }>,
  entry: AuditLogEntry,
  context: ActivityRowContext
) =>
  (change.value === REVIEWING && isUserOpen(entry)) ||
  (change.previousValue === REVIEWING &&
    change.value === TO_REVIEW &&
    closesUserSession(context));
